/**
 * German catalogue.
 *
 * Mirrors `en.ts` exactly — a test fails if the shapes drift.
 *
 * Terminology follows DPMA/EUIPO German usage: "Marke" for trademark,
 * "Widerspruch" for opposition (the statutory term — "Einspruch" is patents),
 * "Inhaber" for the proprietor. German UI also runs noticeably longer than
 * English, so any layout that only fits the English string will break here
 * first; that is a feature of testing in German, not a reason to abbreviate.
 */
import monitoring from './de/monitoring'
import trademarks from './de/trademarks'
import general from './de/general'
import people from './de/people'
import explain from './de/explain'

export default {
  nav: {
    workspace: 'Arbeitsbereich',
    workspaceTools: 'Werkzeuge',
    dashboard: 'Übersicht',
    monitoring: 'Überwachung',
    trademarks: 'Meine Marken',
    oppositions: 'Widersprüche',
    reports: 'Berichte',
    clients: 'Mandanten',
    settings: 'Einstellungen',
  },

  topbar: {
    openMenu: 'Menü öffnen',
    closeMenu: 'Menü schließen',
    search: 'Marken, Inhaber, Klassen suchen…',
    searchLabel: 'Suchen',
    openSearch: 'Globale Suche öffnen',
    notifications: 'Benachrichtigungen',
    notificationsUnread: 'Benachrichtigungen, {n} ungelesen',
    help: 'Hilfe',
    account: 'Konto',
  },

  account: {
    profile: 'Profil',
    settings: 'Einstellungen',
    security: 'Sicherheit',
    help: 'Hilfecenter',
    signOut: 'Abmelden',
    language: 'Sprache',
    menuLabel: 'Konto',
    page: {
      title: 'Ihr Konto',
      subtitle: 'Ihr Name, Ihre Sprache und Ihr Passwort.',
      loadFailed: 'Ihr Konto konnte nicht geladen werden.',
      profile: 'Persönliche Angaben',
      profileSub: 'Wie Kolleginnen und Kollegen Sie sehen, auch im Verlauf der Verfahren.',
      name: 'Vollständiger Name',
      email: 'Anmelde-E-Mail',
      emailHint: 'Ihre Anmeldeadresse ändert die Kanzlei, nicht diese Seite.',
      tier: 'Kontoart',
      firm: 'Kanzlei',
      tiers: {
        firm_main: 'Hauptkonto der Kanzlei',
        firm_manager: 'Teilkonto',
        client_viewer: 'Mandantenkonto (nur lesen)',
      },
      save: 'Angaben speichern',
      saved: 'Angaben gespeichert',
      failed: 'Die Angaben wurden nicht gespeichert',
      nameRequired: 'Geben Sie Ihren Namen ein.',
      password: 'Passwort',
      passwordSub: 'Zum Ändern wird zuerst Ihr aktuelles Passwort abgefragt.',
      current: 'Aktuelles Passwort',
      new: 'Neues Passwort',
      repeat: 'Neues Passwort wiederholen',
      minLength: 'Mindestens {n} Zeichen.',
      tooShort: 'Das neue Passwort braucht mindestens {n} Zeichen.',
      needCurrent: 'Geben Sie Ihr aktuelles Passwort ein.',
      mismatch: 'Die beiden neuen Passwörter stimmen nicht überein.',
      change: 'Passwort ändern',
      changed: 'Passwort geändert',
      wrongCurrent: 'Ihr aktuelles Passwort ist nicht korrekt.',
      changeFailed: 'Das Passwort wurde nicht geändert',
      notYet:
        'Zwei-Faktor-Anmeldung, eine Liste angemeldeter Geräte und API-Tokens gibt es in der App noch nicht.',
    },
  },

  auth: {
    title: 'Willkommen zurück',
    subtitle: 'Melden Sie sich an, um Ihr Markenportfolio zu verwalten.',
    email: 'Geschäftliche E-Mail',
    // `{'@'}` is vue-i18n's literal escape: a bare @ is parsed as a link.
    emailPlaceholder: "sie{'@'}kanzlei.de",
    password: 'Passwort',
    remember: 'Angemeldet bleiben',
    forgot: 'Passwort vergessen?',
    signIn: 'Anmelden',
    signingIn: 'Anmeldung…',
    noAccount: 'Noch kein Konto?',
    requestAccess: 'Zugang anfordern',
    welcomeBack: 'Willkommen zurück',
    failed: 'Anmeldung fehlgeschlagen',
    checkCredentials: 'Bitte prüfen Sie Ihre Zugangsdaten und versuchen Sie es erneut.',
    enterEmail: 'Bitte E-Mail eingeben',
    enterPassword: 'Bitte Passwort eingeben',
    reset: {
      title: 'Passwort zurücksetzen',
      sub: 'Geben Sie die E-Mail-Adresse ein, mit der Sie sich anmelden. Gibt es dazu ein Konto, schicken wir einen Link für ein neues Passwort.',
      submit: 'Link senden',
      sending: 'Wird gesendet…',
      sent: 'Wenn es zu dieser Adresse ein Konto gibt, ist ein Link unterwegs.',
      sentSub:
        'Der Link funktioniert einmal, 30 Minuten lang. Kommt nichts an, prüfen Sie den Spam-Ordner oder fordern Sie ihn erneut an.',
      back: 'Zurück zur Anmeldung',
      failed: 'Die Anfrage konnte nicht gesendet werden. Versuchen Sie es gleich noch einmal.',
    },
    link: {
      checking: 'Link wird geprüft…',
      invalid: 'Dieser Link ist nicht mehr gültig',
      invalidSub: 'Ein Link funktioniert einmal und läuft ab. Fordern Sie unten einen neuen an.',
      askNew: 'Neuen Link anfordern',
      unreachable: 'Der Link konnte nicht geprüft werden',
      resetTitle: 'Neues Passwort für {email}',
      inviteTitle: 'Wählen Sie Ihr Passwort für {email}',
      resetSub: 'Das neue Passwort ersetzt das alte, und Sie sind sofort angemeldet.',
      inviteSub:
        'Sie wurden zu Lexentiqa eingeladen. Wählen Sie ein Passwort, und Sie sind sofort angemeldet.',
      expires: 'Dieser Link läuft am {when} ab.',
      password: 'Neues Passwort',
      confirm: 'Passwort wiederholen',
      minLength: 'Mindestens {n} Zeichen.',
      tooShort: 'Das Passwort braucht mindestens {n} Zeichen.',
      mismatch: 'Die beiden Passwörter stimmen nicht überein.',
      submit: 'Passwort setzen und anmelden',
      submitting: 'Wird gespeichert…',
      done: 'Passwort gesetzt — Sie sind angemeldet',
      failed: 'Das Passwort wurde nicht gesetzt',
    },
  },

  common: {
    save: 'Speichern',
    cancel: 'Abbrechen',
    confirm: 'Bestätigen',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    close: 'Schließen',
    retry: 'Erneut versuchen',
    loading: 'Wird geladen…',
    search: 'Suchen',
    clearAll: 'Alle zurücksetzen',
    skipToContent: 'Zum Hauptinhalt springen',
  },

  states: {
    emptyTitle: 'Noch nichts vorhanden',
    emptyMessage: 'Keine Treffer für die aktuellen Filter.',
    errorTitle: 'Etwas ist schiefgelaufen',
    errorMessage: 'Die Seite konnte nicht geladen werden.',
    notFoundTitle: 'Seite nicht gefunden',
    offline: 'Sie scheinen offline zu sein.',
  },

  time: {
    dueToday: 'Heute fällig',
    daysLeft: 'Noch {n} Tag | Noch {n} Tage',
    daysOverdue: '{n} Tag überfällig | {n} Tage überfällig',
  },

  /* Die Glocke und die Benachrichtigungsseite (W11). */
  notif: {
    subtitle:
      'Was in Ihren Verfahren und in der Überwachung geschehen ist, das Neueste zuerst. Jede Benachrichtigung öffnet, worum es geht.',
    popupNew: '{n} neu',
    popupEmpty: 'Noch nichts vorhanden.',
    viewAll: 'Alle Benachrichtigungen',
    markAllRead: 'Alle als gelesen markieren',
    markAllTitle: 'Alle Benachrichtigungen als gelesen markieren?',
    markAllBody: 'Das gilt für alle, nicht nur für die auf dieser Seite.',
    markAllDone: 'Alle als gelesen markiert',
    settingsLink: 'Benachrichtigungseinstellungen',
    failed: 'Diese Änderung wurde nicht gespeichert',
    loadFailed: 'Die Benachrichtigungen konnten nicht geladen werden.',
    count: '{n} Benachrichtigung | {n} Benachrichtigungen',
    unreadCount: '{n} ungelesen',
    unread: 'ungelesen',
    markRead: 'Als gelesen markieren',
    remove: 'Benachrichtigung löschen',
    range: '{from}–{to} von {total}',
    prev: 'Vorherige Seite',
    next: 'Nächste Seite',
    tabs: {
      all: 'Alle',
      unread: 'Ungelesen',
      match_found: 'Treffer',
      deadline_approaching: 'Fristen',
      procedure_update: 'Verfahrensänderungen',
      system: 'Ankündigungen',
    },
    empty: {
      allTitle: 'Noch keine Benachrichtigungen',
      allMessage:
        'Wenn die Überwachung einen Treffer mit hohem Risiko findet, eine Frist in einem Verfahren näher rückt oder sich ein Verfahren ändert, an dem Sie arbeiten, erscheint es hier.',
      unreadTitle: 'Alles gelesen',
      unreadMessage: 'Nichts wartet darauf, gelesen zu werden.',
      kindTitle: 'Nichts dieser Art',
      kindMessage: 'Ihnen wurde nichts dieser Art geschickt.',
    },
  },

  prefs: {
    title: 'Benachrichtigungseinstellungen',
    sub: 'Für jede Art von Ereignis: in der App, per E-Mail oder beides.',
    event: 'Ereignis',
    channel: {
      in_app: 'In der App',
      email: 'Per E-Mail',
    },
    kind: {
      match_found: {
        title: 'Treffer mit hohem Risiko',
        sub: 'Neue Treffer mit hohem Risiko zu Marken, die Sie sehen — eine Zusammenfassung pro Scan, nicht eine pro Treffer.',
      },
      deadline_approaching: {
        title: 'Fristen in Verfahren',
        sub: 'Eine Woche vorher, am Vortag, am Tag selbst und nach Ablauf — für Verfahren, die Ihnen zugewiesen sind.',
      },
      procedure_update: {
        title: 'Verfahrensänderungen',
        sub: 'Statuswechsel, Notizen, Dokumente und Fristen in Verfahren, die Ihnen zugewiesen sind — nie Ihre eigenen Schritte.',
        viewer: 'Was die Kanzlei zu Ihren Verfahren mit Ihnen teilt.',
      },
      system: {
        title: 'Ankündigungen',
        sub: 'Hinweise zur Plattform selbst, etwa geplante Wartung.',
      },
    },
    defaultNote: 'Ein Schalter, den Sie nie geändert haben, ist eingeschaltet.',
    emailNote:
      'Ob überhaupt E-Mails verschickt werden, entscheidet der Betreiber für die ganze Plattform; diese Schalter entscheiden nur, ob Sie sie erhalten.',
    failed: 'Ihre Benachrichtigungseinstellung wurde nicht gespeichert',
    loadFailed: 'Ihre Benachrichtigungseinstellungen konnten nicht geladen werden.',
  },

  reports: {
    title: 'Scan-Berichte',
    subtitle:
      'Ein Bericht für jeden Tag, an dem die Überwachung Ihre Marken geprüft hat: was sie verglichen und was sie gefunden hat.',
    dayCount: '{n} Tag mit Bericht | {n} Tage mit Bericht',
    newestFirst: 'Neueste zuerst',
    loadFailed: 'Die Berichte konnten nicht geladen werden.',
    emptyTitle: 'Noch keine Scans',
    emptyMessage:
      'Sobald die Überwachung Ihre Marken prüft, bekommt jeder solche Tag hier einen Bericht.',
    col: {
      day: 'Tag',
      brands: 'Geprüfte Marken',
      compared: 'Verglichene Anmeldungen',
      matches: 'Gefundene Treffer',
      high: 'Hohes Risiko',
      judged: 'Vom KI-Prüfer gelesen',
    },
    noScan: 'Kein Scan aufgezeichnet',
    open: 'Bericht vom {day} öffnen',
    pdfFor: 'Bericht vom {day} als PDF herunterladen',
    htmlFor: 'Bericht vom {day} als Druckseite herunterladen',
    range: '{from}–{to} von {total}',
    prev: 'Vorherige Seite',
    next: 'Nächste Seite',
    note: 'Verglichene Anmeldungen zählt die Paare, die das System gegen Ihre Marken bewertet hat; Treffer sind die Paare, die es behalten hat. Ein Tag mit Treffern, aber ohne aufgezeichneten Scan, stammt aus der Zeit, bevor Scans protokolliert wurden.',
    pdf: 'PDF herunterladen',
    html: 'Druckseite',
    pdfUnavailable: 'Dieser Server kann derzeit keine PDFs erzeugen',
    pdfUnavailableSub:
      'Die Druckseite hat denselben Inhalt: Laden Sie sie herunter und drucken Sie sie aus dem Browser.',
    pdfUnavailableCase:
      'Der Betreiber der Plattform kann die PDF-Ausgabe auf dem Server einschalten.',
    downloadFailed: 'Der Download ist fehlgeschlagen',
    state: {
      new: 'Neu',
      reviewed: 'Geprüft',
      dismissed: 'Verworfen',
      escalated: 'Verfahren eröffnet',
    },
    detail: {
      title: 'Scan-Bericht · {day}',
      noRun: 'An diesem Tag wurde kein Scan aufgezeichnet, wohl aber die Treffer unten.',
      ran: 'Lief {from}–{to} UTC',
      runs: '{n} Scan-Lauf | {n} Scan-Läufe',
      totals: 'Der Tag in Zahlen',
      comparedHint: 'Bewertete Kandidatenpaare',
      judgedHint: 'Die übrigen wurden durch Trefferregeln entschieden',
      matchesTitle: 'Gefundene Treffer',
      matchesSub: 'Alle an diesem Tag aufgezeichneten Treffer, die Sie sehen können.',
      noMatches: 'An diesem Tag wurde kein Treffer aufgezeichnet.',
      brandsTitle: 'Geprüfte Marken',
      brandsSub:
        'Jede Marke, die die Scans des Tages verglichen haben, die mit den meisten Treffern mit hohem Risiko zuerst.',
      noBrands: 'An diesem Tag wurde keine Marke geprüft.',
      col: {
        trademark: 'Marke',
        found: 'Gefunden',
        registry: 'Register',
        risk: 'Risiko',
        decidedBy: 'Entschieden durch',
        state: 'Prüfung',
        deadline: 'Widerspruchsfrist',
        client: 'Mandant',
        registries: 'Register',
      },
      judge: 'KI-Prüfer',
      rules: 'Trefferregel',
      showAll: 'Alle {n} anzeigen',
      showFewer: 'Weniger anzeigen',
      truncated: 'Aufgeführt sind die ersten {shown} von {total} Treffern.',
      back: 'Zurück zu den Berichten',
      loadFailed: 'Dieser Bericht konnte nicht geladen werden.',
    },
  },

  settings: {
    title: 'Einstellungen',
    subtitle: 'Ihre Sprache und Benachrichtigungen sowie die Registeranbindungen der Kanzlei.',
    subtitleViewer: 'Ihre Sprache und Ihre Benachrichtigungen.',
    sections: 'Bereiche der Einstellungen',
    mainOnly: 'Nur das Hauptkonto der Kanzlei kann dies ändern.',
    nav: {
      language: 'Sprache',
      notifications: 'Benachrichtigungen',
      firm: 'Kanzlei',
      registries: 'Nationale Register',
      people: 'Personen',
      later: 'Noch nicht in der App',
    },
    language: {
      title: 'Sprache',
      sub: 'Die Sprache dieser App und der E-Mails und Benachrichtigungen, die Sie erhalten.',
      emails: 'E-Mails und Benachrichtigungen erhalten Sie auf {language}.',
      mismatch: 'Ihre E-Mails werden noch auf {stored} geschrieben.',
      useCurrent: 'Auch sie auf {language} schreiben',
      failed:
        'Die Sprache wurde hier geändert, aber nicht in Ihrem Konto gespeichert; E-Mails behalten ihre Sprache.',
    },
    firm: {
      title: 'Kanzlei',
      sub: 'Ihre Kanzlei, wie sie auf gedruckten Berichten erscheint.',
      name: 'Name der Kanzlei',
      email: 'Kontakt-E-Mail',
      save: 'Kanzleiangaben speichern',
      saved: 'Kanzleiangaben gespeichert',
      failed: 'Die Kanzleiangaben wurden nicht gespeichert',
      nameRequired: 'Geben Sie den Namen der Kanzlei ein.',
      loadFailed: 'Die Kanzleiangaben konnten nicht geladen werden.',
    },
    registries: {
      title: 'Nationale Register',
      sub: 'Wie die Plattform das Markenamt eines Landes erreicht, für die nationale Überwachung.',
      credentials:
        'Passwörter und Schlüssel werden hier nie eingegeben. Der Betreiber der Plattform richtet sie auf dem Server ein.',
      add: 'Register hinzufügen',
      country: 'Ländercode',
      countryHint: 'Zwei Buchstaben, etwa BG.',
      type: 'Anbindung',
      kind: {
        api: 'Web-API',
        ftp: 'FTP-Server',
      },
      address: 'Adresse',
      addressHint: 'Die Server- oder Webadresse des Registers — ohne Benutzername und Passwort.',
      useIt: 'Diese Anbindung verwenden',
      save: 'Anbindung speichern',
      badCountry: 'Geben Sie einen zweistelligen Ländercode ein.',
      exists: 'Dieses Land ist bereits angebunden; bearbeiten Sie die Anbindung.',
      saved: 'Anbindung {country} gespeichert',
      failed: 'Die Anbindung wurde nicht gespeichert',
      remove: 'Entfernen',
      removeTitle: 'Anbindung {country} entfernen?',
      removeBody:
        'Die Plattform verwendet sie nicht mehr. Zugangsdaten beim Betreiber der Plattform bleiben unberührt.',
      removed: 'Anbindung entfernt',
      loadFailed: 'Die Registeranbindungen konnten nicht geladen werden.',
      empty: 'Kein nationales Register angebunden',
      emptyMessage:
        'Die nationale Überwachung beobachtet das Markenamt eines Landes. Eine Anbindung hält fest, wie es erreicht wird.',
      active: 'In Verwendung',
      paused: 'Nicht in Verwendung',
      credOnFile: 'Zugangsdaten: vom Betreiber der Plattform eingerichtet.',
      credMissing:
        'Zugangsdaten: noch nicht eingerichtet — wenden Sie sich an den Betreiber der Plattform.',
      adapterYes: 'Die Plattform kann dieses Register lesen.',
      adapterNo:
        'Die Plattform kann dieses Register noch nicht lesen. Die Anbindung ist gespeichert; ein nationaler Scan läuft erst, wenn sie es kann.',
      other: 'Außerdem gespeichert: {keys}',
      updated: 'Aktualisiert am {date}',
    },
    people: {
      title: 'Personen',
      sub: 'Kolleginnen, Kollegen und Personen bei Mandanten, die sich anmelden können, und was jede von ihnen sieht.',
      open: 'Personen verwalten',
      mainOnly: 'Nur das Hauptkonto der Kanzlei fügt Personen hinzu und legt fest, was sie sehen.',
    },
    later: {
      title: 'Noch nicht in der App',
      sub: 'Oft gefragt und auf dieser Seite noch nicht möglich.',
      signinTitle: 'Anmeldesicherheit',
      signin: 'Zwei-Faktor-Anmeldung und Single Sign-on sind noch nicht verfügbar.',
      matchingTitle: 'Wie Konflikte bewertet werden',
      matching:
        'Das Abgleichsystem wird vom Betreiber für die ganze Plattform eingestellt, nicht pro Kanzlei.',
    },
  },

  /* Widerspruchsverfahren (W10). „Widerspruch" ist der markenrechtliche
   * Begriff (nicht „Einspruch"). Registercodes (EUIPO, WIPO) kommen aus den
   * Daten: Es sind Namen, sie werden nicht übersetzt. */
  opp: {
    title: 'Widerspruchsverfahren',
    caseRef: 'Verfahren {ref}',
    registryBg: 'Bulgarisches Patentamt',
    status: {
      under_review: 'In Prüfung',
      drafting: 'In Ausarbeitung',
      filed: 'Widerspruch eingelegt',
      awaiting_response: 'Wartet auf Antwort',
      negotiation: 'Verhandlung',
      resolved: 'Entschieden',
      closed: 'Abgeschlossen',
    },
    outcome: {
      won: 'Gewonnen',
      settled: 'Vergleich',
      lost: 'Verloren',
      withdrawn: 'Zurückgenommen',
    },
    priority: {
      urgent: 'Dringend',
      high: 'Hoch',
      medium: 'Mittel',
      low: 'Niedrig',
    },
    risk: {
      high: 'Hohes Risiko',
      med: 'Mittleres Risiko',
      low: 'Geringes Risiko',
      none: 'Diesem Verfahren liegt kein Treffer zugrunde, daher gibt es keine Risikobewertung',
    },
    verdict: {
      conflict: 'Kollision',
      strong_match: 'Kollision',
      unclear: 'Menschliche Entscheidung nötig',
      review: 'Menschliche Entscheidung nötig',
      no_conflict: 'Keine Kollision',
      dismiss: 'Keine Kollision',
    },
    kind: {
      statutory: 'Gesetzlich',
      internal: 'Intern',
    },
    noteCategory: {
      legal: 'Rechtlich',
      internal: 'Intern',
      status: 'Statusänderung',
    },
    docCategory: {
      filing: 'Einreichung',
      evidence: 'Beweismittel',
      correspondence: 'Schriftverkehr',
      decision: 'Entscheidung',
      agreement: 'Vereinbarung',
      other: 'Sonstiges',
    },
    deadline: {
      provisional: 'Vorläufig: Regel wird geprüft',
      provisionalShort: 'vorläufig',
      provisionalHelp:
        'Dieses Datum folgt einer Registerregel, die die Anwälte der Kanzlei noch nicht bestätigt haben. Prüfen Sie es, bevor Sie sich darauf verlassen.',
      none: 'Keine offene Frist',
      finished: 'Verfahren beendet',
      done: 'Erledigt am {date}',
      fromMark: 'Aus der Veröffentlichung des Registers übernommen',
      fromRule: 'Durch eine Fristenregel gesetzt',
      fromFirm: 'Von der Kanzlei eingetragen',
    },
    list: {
      subtitle:
        'Jede Kollision, zu der die Kanzlei eine Akte angelegt hat: ihr Stand, ihre nächste Frist und wer daran arbeitet.',
      filters: 'Filter',
      create: 'Widerspruchsverfahren anlegen',
      cases: '{n} Verfahren | {n} Verfahren',
      filtersOn: '{n} Filter aktiv | {n} Filter aktiv',
      search: 'Titel, Aktenzeichen, Marke oder Mandant suchen…',
      searchLabel: 'Verfahren suchen',
      range: '{from}–{to} von {total}',
      prev: 'Vorherige Seite',
      next: 'Nächste Seite',
      emptyTitle: 'Noch keine Widerspruchsverfahren',
      emptyMessage:
        'Ein Verfahren wird aus einem Treffer auf der Überwachungsseite angelegt oder von Hand auf dieser Seite.',
      toMonitoring: 'Zur Überwachung',
      noMatchTitle: 'Keine Verfahren passen zu diesen Filtern',
      noMatchMessage: 'Setzen Sie die Filter zurück, um alle Verfahren zu sehen.',
      clearFilters: 'Filter zurücksetzen',
      loadFailed: 'Die Verfahren konnten nicht geladen werden.',
    },
    kpi: {
      active: 'Aktive Verfahren',
      openedThisMonth: '{n} in diesem Monat angelegt',
      urgent: 'Überfällig oder binnen 14 Tagen fällig',
      overdue: '{n} überfällig',
      due14: '{n} binnen 14 Tagen fällig',
      awaiting: 'Wartet auf Antwort',
      awaitingHint: 'Eingelegt, wartet auf die Gegenseite',
      won: 'Dieses Jahr gewonnen oder verglichen',
      wonHint: 'von {n} in diesem Jahr entschiedenen Verfahren',
    },
    filter: {
      registry: 'Register',
      status: 'Verfahrensstand',
      deadline: 'Nächste Frist',
      deadlineNote: 'Gezählt über aktive Verfahren.',
      overdue: 'Überfällig',
      within: 'Binnen {n} Tagen',
      any: 'Beliebiges Datum',
      priority: 'Priorität',
      activeOnly: 'Nur aktive Verfahren',
      client: 'Mandant: {name}',
      brand: 'Marke: {name}',
      search: 'Suche: {q}',
    },
    sort: {
      label: 'Sortierung',
      deadline: 'Nächste Frist zuerst',
      recent: 'Zuletzt aktualisiert',
      priority: 'Höchste Priorität zuerst',
      created: 'Neueste Verfahren zuerst',
    },
    col: {
      conflict: 'Markenkollision',
      risk: 'Risiko des Treffers',
      status: 'Verfahrensstand',
      deadline: 'Nächste Frist',
      lawyer: 'Anwalt',
    },
    preview: {
      label: 'Verfahrensvorschau',
      show: 'In der Vorschau zeigen',
      close: 'Vorschau schließen',
      origin: 'Woher dieses Verfahren stammt',
      judged: 'Vom KI-Prüfer gelesen',
      rules: 'Durch eine Trefferregel gefunden',
      byHand:
        'Von Hand angelegt. Kein Treffer liegt ihm zugrunde, daher hat es keine Risikobewertung.',
      viewMatch: 'Ansehen, wie der Treffer gefunden wurde',
      nextDeadline: 'Nächste Frist',
      open: 'Akte öffnen',
    },
    fact: {
      lawyer: 'Zuständiger Anwalt',
      unassigned: 'Nicht zugewiesen',
      client: 'Mandant',
      documents: 'Dokumente',
      files: '{n} Datei | {n} Dateien',
      notes: 'Notizen',
      noteCount: '{n} Notiz | {n} Notizen',
      lastActivity: 'Letzte Aktivität',
      grounds: 'Widerspruchsgründe',
      notRecorded: 'Noch nicht erfasst',
      caseNumber: 'Aktenzeichen',
      priority: 'Priorität',
      registry: 'Register',
      filed: 'Einreichungsdatum',
      createdBy: 'Angelegt von',
      internalNotes: 'Arbeitsnotizen',
    },
    activity: {
      title: 'Aktuelle Aktivität',
      sub: 'Die neuesten Änderungen in allen Verfahren, die Sie sehen können.',
      empty: 'In keinem Verfahren ist bisher etwas geschehen.',
    },
    event: {
      created: 'Verfahren von Hand angelegt',
      createdFromMatch: 'Verfahren aus einem Überwachungstreffer angelegt',
      status: 'Stand geändert von „{from}“ zu „{to}“',
      statusOutcome: 'Stand geändert von „{from}“ zu „{to}“: {outcome}',
      note: 'Notiz hinzugefügt ({category})',
      documentAdded: 'Dokument hinzugefügt: {name}',
      documentRemoved: 'Dokument entfernt: {name}',
      documentUpdated: 'Dokumentangaben geändert: {name}',
      assigned: '{name} zugewiesen',
      assignedSomeone: 'Zuständiger Anwalt geändert',
      unassigned: 'Zuständiger Anwalt entfernt',
      deadlineAdded: 'Frist hinzugefügt: {title}, fällig am {date}',
      deadlineDone: 'Frist erledigt: {title}',
      deadlineReopened: 'Frist wieder geöffnet: {title}',
      deadlineEdited: 'Frist geändert: {title}',
      deadlineRemoved: 'Frist entfernt: {title}',
      priority: 'Priorität geändert von „{from}“ zu „{to}“',
      edited: 'Verfahrensangaben bearbeitet',
      windowClosed:
        'Die Widerspruchsfrist gegen diese Marke ist am {date} abgelaufen, bevor das Verfahren angelegt wurde',
      other: 'Verfahren aktualisiert',
      someone: 'Jemand',
      clientVisible: 'Für den Mandanten sichtbar',
    },
    create: {
      crumb: 'Neues Verfahren',
      title: 'Widerspruchsverfahren anlegen',
      subtitle:
        'Ein Verfahren ist die Akte zu einer Kollision: die Marke Ihres Mandanten gegen eine Anmeldung.',
      submit: 'Verfahren anlegen',
      submitting: 'Wird angelegt…',
      draftFailed:
        'Dieser Treffer konnte nicht geladen werden. Er wurde vielleicht entfernt, oder Sie haben keinen Zugriff darauf.',
      existingTitle: 'Zu diesem Treffer gibt es bereits ein offenes Verfahren',
      existingBody:
        'Ein Treffer kann nur ein offenes Verfahren zugleich haben. Arbeiten Sie im bestehenden weiter.',
      openExisting: 'Bestehendes Verfahren öffnen',
      conflictSection: 'Die Kollision',
      conflictFromMatch:
        'Aus dem Treffer übernommen. Was die Kollision bestimmt, kann hier nicht geändert werden.',
      analysisSection: 'Was der Abgleich gefunden hat',
      judgeScore: 'Verwechslungswahrscheinlichkeit laut KI-Prüfer',
      ruleScore: 'Ähnlichkeit aus einer Trefferregel, keine Wahrscheinlichkeit',
      noJustification:
        'Eine Regel hat dieses Paar gefunden; kein Modell hat eine Begründung verfasst.',
      pickSection: 'Die Marke Ihres Mandanten',
      client: 'Mandant',
      clientSearch: 'Mandanten suchen…',
      chooseClient: 'Mandanten wählen',
      brand: 'Marke',
      brandSearch: 'In den Marken dieses Mandanten suchen…',
      chooseBrand: 'Marke wählen',
      noBrands: 'Dieser Mandant hat noch keine Marken.',
      narrow: '{shown} von {total} angezeigt. Tippen Sie, um die Liste einzugrenzen.',
      opposedSection: 'Die angegriffene Marke',
      markName: 'Name der Marke',
      markNumber: 'Anmeldenummer',
      markOwner: 'Anmelder oder Inhaber',
      registry: 'Register',
      registryNone: 'Nicht angegeben',
      handNote:
        'Einem von Hand angelegten Verfahren liegt kein Treffer zugrunde: Es hat weder Risikobewertung noch eine Frist aus einem Register. Tragen Sie die Frist auf der Verfahrensseite ein.',
      caseSection: 'Das Verfahren',
      caseTitle: 'Titel des Verfahrens',
      caseTitleHint: 'Bleibt das Feld leer, heißt das Verfahren „{title}“.',
      caseNumber: 'Aktenzeichen',
      caseNumberHint: 'Das Aktenzeichen des Amtes, sobald es eines gibt.',
      priority: 'Priorität',
      startAs: 'Beginnt als',
      lawyer: 'Zuständiger Anwalt',
      grounds: 'Widerspruchsgründe',
      groundsPlaceholder: 'z. B. Art. 8 Abs. 1 Buchst. b UMV',
      notes: 'Interne Notizen',
      notesHint: 'Die Arbeitsnotizen der Kanzlei. Werden dem Mandanten nie gezeigt.',
      markDeadline:
        'Die Widerspruchsfrist der gefundenen Marke, der {date}, wird die erste Frist des Verfahrens.',
      markDeadlinePassed:
        'Die Widerspruchsfrist gegen diese Marke ist am {date} abgelaufen. Das Verfahren hält das fest, statt eine überfällige Frist zu führen.',
      afterNote:
        'Dokumente, Fristen und Notizen werden auf der Verfahrensseite hinzugefügt, sobald es angelegt ist.',
      pickBrand: 'Wählen Sie die Marke Ihres Mandanten.',
      nameMark: 'Geben Sie die angegriffene Marke an.',
      failed: 'Das Verfahren wurde nicht angelegt',
      created: 'Verfahren angelegt',
      lawyersFailed:
        'Die Anwaltsliste konnte nicht geladen werden. Das Verfahren kann später zugewiesen werden.',
    },
    detail: {
      assigned: 'Zuständig: {name}',
      filed: 'Eingereicht am {date}',
      backToCases: 'Zurück zu den Verfahren',
      addNote: 'Notiz hinzufügen',
      upload: 'Dokument hochladen',
      uploading: 'Wird hochgeladen…',
      updateStatus: 'Stand ändern',
      delete: 'Verfahren löschen',
      deleteTitle: 'Dieses Verfahren löschen?',
      deleteBody:
        'Das Verfahren, sein Verlauf, seine Notizen und Dateien werden endgültig entfernt. Um die Arbeit zu beenden, aber den Verlauf zu behalten, schließen Sie es stattdessen ab.',
      deleted: 'Verfahren gelöscht',
      deleteFailed: 'Das Verfahren wurde nicht gelöscht',
      readOnly: 'Sie können dieses Verfahren lesen, aber nicht ändern.',
      sections: 'Abschnitte des Verfahrens',
      tabConflict: 'Kollision',
      tabAnalysis: 'Analyse',
      tabNotes: 'Notizen',
      tabDocs: 'Dokumente',
      tabTimeline: 'Verlauf',
      protected: 'Die Marke Ihres Mandanten',
      opposed: 'Angegriffene Marke',
      yourClient: 'Ihr Mandant',
      opposedTag: 'Angegriffen',
      applicationNo: 'Anmeldenummer',
      registered: 'Eingetragen',
      filedOn: 'Angemeldet',
      classes: 'Nizza-Klassen',
      type: 'Markenform',
      owner: 'Inhaber',
      windowCloses: 'Widerspruchsfrist',
      handTyped: 'Von Hand eingegeben: Bekannt ist nur, was der Anwalt eingetragen hat.',
      analysisJudge: 'Vom KI-Prüfer gelesen. Sein Urteil und seine eigenen Worte stehen unten.',
      analysisRules:
        'Durch eine Trefferregel gefunden. Kein Modell hat dieses Paar gelesen, und die Zahl ist eine Ähnlichkeit, keine Wahrscheinlichkeit.',
      score: '{n} von 100',
      verdict: 'Einschätzung',
      overlap: 'Gemeinsame Nizza-Klassen',
      noOverlap: 'Keine gemeinsame Nizza-Klasse',
      onlyTheirs: 'nur deren',
      loadFailed: 'Ein Teil dieses Verfahrens konnte nicht geladen werden.',
      editDetails: 'Bearbeiten',
      saveDetails: 'Angaben speichern',
      detailsSaved: 'Verfahrensangaben gespeichert',
      detailsFailed: 'Die Verfahrensangaben wurden nicht gespeichert',
      summaryPdf: 'Verfahrenszusammenfassung (PDF)',
    },
    notes: {
      title: 'Notizen zum Verfahren',
      sub: 'Rechtliche Feststellungen, interne Arbeitsnotizen und Statusänderungen.',
      all: 'Alle',
      placeholder: 'Rechtliche Notiz, Feststellung oder Anweisung an das Team schreiben…',
      visible: 'Für den Mandanten sichtbar',
      visibleHint: 'Das Lesekonto des Mandanten sieht diese Notiz.',
      post: 'Notiz speichern',
      posting: 'Wird gespeichert…',
      empty: 'Noch keine Notizen.',
      emptyCategory: 'Keine Notizen in dieser Kategorie.',
      added: 'Notiz hinzugefügt',
      failed: 'Die Notiz wurde nicht gespeichert',
    },
    docs: {
      title: 'Dokumente',
      sub: 'Einreichungen, Beweismittel, Schriftverkehr und Entscheidungen.',
      category: 'Kategorie',
      choose: 'Datei hochladen',
      hint: 'PDF, Word, reiner Text oder ein Bild.',
      visibleShort: 'Mandant sieht es',
      hiddenShort: 'Nur Kanzlei',
      showToClient: 'Dem Mandanten zeigen',
      hideFromClient: 'Vor dem Mandanten verbergen',
      download: '{name} herunterladen',
      delete: '{name} löschen',
      deleteTitle: '{name} löschen?',
      deleteBody:
        'Die Datei wird aus diesem Verfahren entfernt. Das kann nicht rückgängig gemacht werden.',
      empty: 'Keine Dokumente in diesem Verfahren.',
      uploaded: '{name} hochgeladen',
      failed: 'Das Hochladen ist fehlgeschlagen',
      actionFailed: 'Diese Änderung am Dokument wurde nicht gespeichert',
    },
    timeline: {
      title: 'Verfahrensverlauf',
      sub: 'Jede Änderung an diesem Verfahren, die neueste zuerst. Der Verlauf kann nicht bearbeitet werden.',
      empty: 'Noch nichts erfasst.',
    },
    lifecycle: {
      title: 'Verfahrensstand',
      moveTo: 'Wechseln zu',
      choose: 'Nächsten Schritt wählen',
      outcome: 'Wie ist es ausgegangen?',
      chooseOutcome: 'Ausgang wählen',
      reason: 'Warum wird das Verfahren abgeschlossen?',
      reopenReason: 'Warum wird es wieder geöffnet? (optional)',
      filingDate: 'Einreichungsdatum',
      clientVisible: 'Diesen Schritt dem Mandanten zeigen',
      apply: 'Stand ändern',
      applying: 'Wird gespeichert…',
      needOutcome: 'Geben Sie an, wie das Verfahren ausgegangen ist.',
      needReason: 'Geben Sie an, warum das Verfahren abgeschlossen wird.',
      changed: 'Stand geändert zu „{status}“',
      failed: 'Der Stand wurde nicht geändert',
      noMoves: 'Von hier aus gibt es keine weiteren Schritte.',
      outcomeLine: 'Ausgang: {outcome}',
    },
    deadlines: {
      title: 'Fristen',
      empty: 'Keine Fristen in diesem Verfahren.',
      markDone: 'Als erledigt markieren',
      reopen: 'Wieder öffnen',
      delete: 'Frist löschen',
      deleteTitle: '„{title}“ löschen?',
      deleteBody: 'Die Frist wird entfernt. Der Verlauf hält fest, dass es sie gab.',
      add: 'Frist hinzufügen',
      adding: 'Wird hinzugefügt…',
      newTitle: 'Was fällig ist',
      newDate: 'Fälligkeitsdatum',
      newKind: 'Art',
      newNote: 'Notiz (optional)',
      failed: 'Die Frist wurde nicht gespeichert',
    },
    related: {
      title: 'Widerspruchsverfahren',
      count: '{n} Verfahren | {n} Verfahren',
      none: 'Noch keine Widerspruchsverfahren.',
      viewAll: 'Alle ansehen',
      create: 'Widerspruch anlegen',
      failed: 'Die Verfahren konnten nicht geladen werden.',
    },
    match: {
      openCase: 'Widerspruchsverfahren anlegen',
      openCaseHint:
        'Legt ein aus diesem Treffer vorausgefülltes Verfahren an. Ein Treffer kann nur ein offenes Verfahren zugleich haben.',
    },
  },

  clients: {
    title: 'Mandanten und Markeninhaber',
    subtitle:
      'Die Unternehmen, deren Marken die Kanzlei schützt: ihre Marken, ihre Überwachung und ihre Verfahren.',
    filters: 'Filter',
    add: 'Mandant hinzufügen',
    unit: 'Mandanten',
    shown: '{n} von {total} Mandanten',
    filtersActive: '{n} Filter aktiv | {n} Filter aktiv',
    searchPlaceholder: 'Mandanten suchen…',
    unassigned: 'Nicht zugewiesen',
    kpi: {
      label: 'Überblick über die Mandanten',
      total: 'Mandanten',
      newThisQuarter: 'neu in diesem Quartal',
      trademarks: 'Marken',
      perClient: 'durchschnittlich {n} je Mandant',
      highRisk: 'Mandanten mit hohem Risiko',
      highRiskHint: 'Zwei oder mehr offene Verfahren',
      registries: 'Register',
      registriesHint: 'Wo ihre Marken überwacht werden',
    },
    filter: {
      name: 'Name des Mandanten',
      namePlaceholder: 'Unternehmen oder Ansprechpartner',
      registry: 'Register',
      monitoring: 'Überwachung',
      risk: 'Risikostufe',
    },
    chip: {
      risk: 'Risiko: {level}',
      search: 'Suche: {q}',
    },
    risk: {
      high: 'Hoch',
      med: 'Mittel',
      low: 'Gering',
      none: 'Nicht bewertet',
    },
    monitoring: {
      full: 'Vollständig überwacht',
      partial: 'Teilweise überwacht',
      paused: 'Überwachung pausiert',
      none: 'Nicht überwacht',
      unknown: 'Überwachung unbekannt',
    },
    sort: {
      label: 'Mandanten sortieren',
      name: 'Name, A bis Z',
      nameDesc: 'Name, Z bis A',
      trademarks: 'Meiste Marken',
      cases: 'Meiste offene Verfahren',
      recent: 'Zuletzt geändert',
    },
    empty: {
      title: 'Keine Mandanten entsprechen Ihrer Suche',
      message: 'Versuchen Sie einen anderen Suchbegriff oder entfernen Sie die aktiven Filter.',
      reset: 'Filter zurücksetzen',
    },
    col: {
      client: 'Mandant',
      registries: 'Register',
      risk: 'Risiko',
      lawyer: 'Anwalt',
      trademarks: 'Marken',
    },
    preview: {
      label: 'Überblick über den Mandanten',
      close: 'Vorschau schließen',
      summary: 'Zusammenfassung',
      registries: '{n} Register | {n} Register',
      monitoring: 'Überwacht',
      openCases: 'Offene Verfahren',
      allMarks: 'Alle {n}',
      noMarks: 'Keine Marken erfasst.',
      registered: 'Eingetragen',
      pending: 'Angemeldet',
      classes: 'Klasse {list} | Klassen {list}',
      lawyers: 'Anwälte',
      activity: 'Letzte Aktivität in Verfahren',
      noActivity: 'Noch keine Aktivität in Verfahren.',
      open: 'Mandantenakte öffnen',
    },
    activity: {
      title: 'Letzte Aktivität in Verfahren',
      sub: 'Die neuesten Einträge aus dem Verlauf jedes Verfahrens, über alle Mandanten.',
      empty: 'Noch keine Aktivität in Verfahren.',
    },
    quick: {
      title: 'Schnellzugriff',
      addClient: 'Ein Unternehmen hinzufügen, dessen Marken die Kanzlei schützt.',
      addTrademark: 'Marke hinzufügen',
      addTrademarkSub:
        'Eine Marke in das Portfolio eines Mandanten aufnehmen und festlegen, wo sie überwacht wird.',
      monitoring: 'Überwachung öffnen',
      monitoringSub: 'Prüfen, was die Überwachung gegen die Marken Ihrer Mandanten gefunden hat.',
      case: 'Verfahren eröffnen',
      caseSub: 'Ein Widerspruchsverfahren für einen Mandanten beginnen.',
    },
  },

  client: {
    back: 'Zurück zu den Mandanten',
    edit: 'Mandant bearbeiten',
    eyebrow: 'Mandant · {id}',
    riskClient: 'Risiko: {level}',
    trademarkCount: '{n} Marke | {n} Marken',
    openCaseCount: '{n} offenes Verfahren | {n} offene Verfahren',
    contact: 'Ansprechpartner: {name}',
    monitoring: 'Überwachung',
    watchedOf: '{n} von {total} Marken überwacht',
    openMonitoring: 'Überwachung öffnen',
    newCase: 'Neues Verfahren',
    sections: 'Abschnitte der Mandantenakte',
    failed: 'Dies konnte nicht geladen werden. Laden Sie die Seite neu, um es erneut zu versuchen.',
    notWatched: 'Nicht überwacht',
    classes: 'Klasse {list} | Klassen {list}',
    registeredOn: 'Eingetragen am {date}',
    kpi: {
      label: 'Überblick über den Mandanten',
      coverage: 'Abdeckung der Überwachung',
      casesOpen: 'Offene Verfahren warten auf eine Antwort',
      casesNone: 'Keine offenen Verfahren',
    },
    tab: {
      portfolio: 'Portfolio',
      cases: 'Verfahren',
      people: 'Personen',
      matches: 'Treffer',
      activity: 'Aktivität',
      monitoring: 'Überwachung',
      documents: 'Dokumente',
    },
    portfolio: {
      title: 'Markenportfolio',
      sub: 'Alle erfassten Marken dieses Mandanten, eingetragen oder angemeldet.',
      all: 'Alle in „Meine Marken“ ansehen',
      emptyTitle: 'Noch keine Marken',
      empty: 'Für diesen Mandanten sind keine Marken erfasst.',
    },
    people: {
      title: 'Personen mit Zugriff',
      sub: 'Die Anwälte, die für diesen Mandanten arbeiten, und die eigenen Nutzer des Mandanten. Die Hauptkonten der Kanzlei sehen alle Mandanten und sind nicht aufgeführt.',
      empty: 'Bisher hat niemand Zugriff auf diesen Mandanten erhalten.',
      add: 'Zugang geben',
      manager: 'Anwalt',
      viewer: 'Nutzer des Mandanten',
      off: 'Deaktiviert',
      wholeCompany: 'Ganzes Unternehmen',
      someMarks: '{n} Marke | {n} Marken',
    },
    matches: {
      title: 'Offene Treffer',
      sub: 'Was die Überwachung gegen die Marken dieses Mandanten gefunden hat, die nächsten Fristen zuerst.',
      lastScan: 'Letzte Prüfung am {date}',
      awaiting: 'warten auf Prüfung',
      high: 'mit hohem Risiko',
      closingSoon: 'mit hohem Risiko und Frist in 14 Tagen',
      stillOpen: 'mit noch offener Frist',
      none: 'Kein Treffer mit noch offener Widerspruchsfrist.',
      against: 'gegen {brand}',
    },
    activity: {
      title: 'Aktivität in Verfahren',
      sub: 'Die neuesten Einträge aus den Widerspruchsverfahren dieses Mandanten.',
      empty: 'Noch keine Aktivität in den Verfahren dieses Mandanten.',
    },
    coverage: {
      title: 'Überwachung nach Register',
      sub: 'Wie viele Marken dieses Mandanten in jedem Register überwacht werden.',
      ofMarks: 'von {n} Marke | von {n} Marken',
      lastScan: 'Letzte Prüfung am {date}',
      waiting: 'Wartet auf die erste Prüfung',
      notWatched: 'Hier wird keine überwacht',
    },
    docs: {
      title: 'Dokumente',
      sub: 'Die Dateien zu den Marken dieses Mandanten. Sie werden auf der Seite der jeweiligen Marke hinzugefügt.',
      empty: 'Keine Dokumente zu den Marken dieses Mandanten.',
      open: '{name} öffnen',
      openFailed: 'Die Datei konnte nicht geöffnet werden',
      kind: {
        logo: 'Logo',
        certificate: 'Urkunde',
        evidence: 'Nachweis',
        correspondence: 'Schriftverkehr',
        other: 'Sonstiges',
      },
    },
  },

  /* The pages translated in the Wave 3 pass (2026-09-30), one file per area
   * so they could be written side by side. */
  ...monitoring,
  ...trademarks,
  ...general,
  ...people,
  ...explain,
} as const
